import React from 'react';
import { useTheme } from '@mui/material';

import Description from 'components/CareSummary/Description';
import Empty from 'components/CareSummary/Empty';
import Title from 'components/CareSummary/Title';
import { LineChart } from 'components/Graphs/LineChart';
import { Container } from 'shared-components/Container';
import { useGetEGFRGraphQuery, useGetEHREGFRQuery } from 'store/api/pkd/endpoints/graphs';
import {
  useGetProfileParticipantTasksQuery,
  useGetProfileParticipantTaskQuery,
} from 'store/api/pkd/endpoints/participantTask';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import { TaskTitles } from 'utils/enum';
import { ChartGraphSkeleton } from '../ChartSkeleton';
import { formatDate } from 'utils/date';

const KidneyFunctionOverTimeChart = () => {
  const theme = useTheme();
  const { isPersonLoading, personId, personAccountProfileId } =
    useGetPersonAndAttributes();

  const { participantTask, isParticipantTaskLoading } =
    useGetProfileParticipantTasksQuery(
      { personAccountProfileId },
      {
        skip             : !personAccountProfileId,
        selectFromResult : ({ data = [], isFetching }) => ({
          participantTask : data?.find(
            (item) => item.taskTitle === TaskTitles.coreQuestionnaire
          ),
          isParticipantTaskLoading : isFetching,
        }),
      }
    );

  const { data: coreQuestionnaire, isLoading: isCoreQuestionnaireLoading } =
    useGetProfileParticipantTaskQuery(
      { personAccountProfileId, participantTaskId: participantTask?.id },
      { skip: !participantTask }
    );

  const { ehrEgfrData, isFetching: isEHREGFRDataLoading } = useGetEHREGFRQuery(
    { personId },
    {
      skip             : !personId,
      selectFromResult : ({ data, isLoading, isFetching }) => {
        const ehrEgfrData = data?.data?.egfrData?.items ?? [];
        return {
          ehrEgfrData,
          isLoading,
          isFetching,
        };
      },
    }
  );

  const { data: egfGraph = [], isFetching: isEGFRGraphLoading } =
    useGetEGFRGraphQuery({ personId }, { skip: !personId });

  const kidneyFOTChart = {
    xAxis : {
      type : 'category',
    },
    yAxis : {
      type     : 'value',
      interval : 20,
      min      : 0,
      max      : 140,
    },
    legend : {
      show       : true,
      icon       : 'circle',
      bottom     : '0',
      width      : 'auto',
      height     : 'auto',
      orient     : 'horizontal',
      align      : 'auto',
      itemWidth  : 30, // correct this
      itemHeight : 10,
      itemGap    : 10,
      data       : [
        (egfGraph?.length > 0)
          ? {
            name      : 'Self-Reported eGFR',
            itemStyle : {
              color       : theme.palette.primary.blue1, // correct this
              borderColor : theme.palette.primary.blue1,
              borderWidth : 2,
            },
            textStyle : {
              color    : '#000',
              fontSize : 14
            },
          }
          : {},
        (ehrEgfrData?.length > 0)
          ? {
            name      : 'EMR Mediated eGFR',
            itemStyle : {
              color       : 'orange', // correct this
              borderColor : 'orange',
              borderWidth : 2,
            },
            textStyle : {
              color    : '#000',
              fontSize : 14
            },
          }
          : {},
      ]
    },
    series : [
      {
        name      : 'Self-Reported eGFR',
        data      : (() => egfGraph?.map(item => ([item.createdAt, item.egfrScore])))(),
        type      : 'line',
        itemStyle : { color: theme.palette.primary.blue1 },
        lineStyle : { color: theme.palette.primary.blue1 },
      },
      {
        name      : 'EMR Mediated eGFR',
        data      : (() => ehrEgfrData?.map(item => ([formatDate(item.effectiveDatetime, 'YYYY-MM-DD'), item.egfr])))(),
        type      : 'line',
        itemStyle : { color: 'orange', borderWidth: 20, width: 20 },
        lineStyle : { color: 'orange', borderWidth: 20, width: 20 },
      },
    ],
  };

  const isLoading =
    isPersonLoading ||
    isCoreQuestionnaireLoading ||
    isEGFRGraphLoading ||
    isParticipantTaskLoading ||
    isEHREGFRDataLoading;

  const content = isLoading
    ? (
      <ChartGraphSkeleton />
    )
    : ((kidneyFOTChart?.xAxis?.data?.length === 0 ||
    !coreQuestionnaire?.taskEntry) && ehrEgfrData.length === 0)
      ? (
        <Empty
          description='Fill out the Core Questionnaire module'
          to={`/questionnaire/${coreQuestionnaire?.participantTaskId}`}
          buttonText='Fill out the Core Questionnaire module'
        />
      )
      : (
        <LineChart
          id='kidney-function-over-time'
          label='eGFR'
          width='550px'
          height='500px'
          option={kidneyFOTChart}
        />
      );

  return (
    <Container>
      <Title>Kidney function over time</Title>
      <Description>
        As your PKD progresses, your kidneys will grow and lose function. This
        is measured through your blood or urine and is called eGFR. Knowing your
        disease progression can help you to plan your future.
      </Description>
      {content}
    </Container>
  );
};

export default KidneyFunctionOverTimeChart;
